// 097 -- Organiser-facing directory of parent-registered offers for this
// school. Category filter + free-text search. Per-offer "Send message"
// opens the 031 DirectMessageDialog pre-filled with the offer title as
// subject.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { VolunteerCategory, VolunteerOfferPublic } from "@dugads/shared";
import { VOLUNTEER_CATEGORIES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { DirectMessageDialog } from "@/components/direct-message-dialog";
import { MessageSquare } from "lucide-react";

const CATEGORY_LABELS: Record<VolunteerCategory, string> = {
  transport: "Transport",
  catering: "Catering",
  scoring: "Scoring",
  setup: "Setup",
  first_aid: "First aid",
  general: "General",
};

export default function SchoolVolunteerDirectoryPage() {
  const { id: schoolId } = useParams<{ id: string }>();
  const [offers, setOffers] = useState<VolunteerOfferPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<"" | VolunteerCategory>("");
  const [search, setSearch] = useState("");
  const [messageTarget, setMessageTarget] = useState<VolunteerOfferPublic | null>(null);

  const reload = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.getSchoolVolunteerOffers(schoolId, {
        category: category || undefined,
        search: search || undefined,
      });
      setOffers(r.offers);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, [schoolId, category, search]);

  useEffect(() => { void reload(); }, [reload]);

  if (!schoolId) return null;
  const schoolName = offers[0]?.school_name ?? "School";

  return (
    <div data-page="school-volunteer-directory" data-state={loading ? "loading" : "ready"} className="flex flex-col gap-6">
      <Breadcrumbs items={[
        { label: "Schools", to: "/schools" },
        { label: schoolName, to: `/schools/${schoolId}` },
        { label: "Volunteers" },
      ]} />
      <h1 className="text-2xl font-semibold">Volunteer offers · {schoolName}</h1>
      <p className="text-sm text-muted-foreground">
        Parents register the things they're willing to help with. Browse here when planning events and reach out directly with a message. No obligation -- this is a directory, not a commitment.
      </p>

      <div className="flex flex-wrap items-end gap-3" data-region="filters">
        <div className="flex flex-col gap-1">
          <Label htmlFor="filter-category">Category</Label>
          <select
            id="filter-category"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value as "" | VolunteerCategory)}
            data-field="category"
          >
            <option value="">All categories</option>
            {VOLUNTEER_CATEGORIES.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <Label htmlFor="filter-search">Search</Label>
          <Input
            id="filter-search"
            placeholder="Title or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-field="search"
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {offers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No offers match your filters yet.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-region="offer-grid">
          {offers.map((o) => (
            <Card key={o.id} data-row={o.id}>
              <CardHeader>
                <CardTitle className="text-base">{o.title}</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  {CATEGORY_LABELS[o.category]} · {o.user_display_name}
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {o.description && (
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{o.description}</p>
                )}
                <div>
                  <Button size="sm" variant="outline" onClick={() => setMessageTarget(o)} data-action="message">
                    <MessageSquare className="mr-1 h-4 w-4" /> Send message
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {messageTarget && (
        <DirectMessageDialog
          schoolId={schoolId}
          schoolName={schoolName}
          initialRecipientIds={[messageTarget.user_id]}
          initialSubject={`Re: ${messageTarget.title}`}
          onClose={() => setMessageTarget(null)}
        />
      )}
    </div>
  );
}
